import { Box, styled, Typography } from "@material-ui/core";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { formatDistanceToNow } from "date-fns";
import { useNavigate } from "react-router-dom";
import { formatDicomPatientName } from "./dicom-patient-name";
import { EXAM_ICONS, EXAM_LABELS } from "./epocus-exam-constants";
import { UltrasoundScanner } from "./epocus-icons";
import {
  EPOCUS_BG,
  EPOCUS_BORDER,
  EPOCUS_ICON_ACTIVE,
  EPOCUS_SELECTED_BG,
  EPOCUS_STATUS_DRAFT_BG,
  EPOCUS_STATUS_DRAFT_TEXT,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "./epocus-palette";
import { linkToReviewQueueEntry } from "./review-queue-destination";
import type { ReviewerQueueEntry } from "./use-reviewer-queue";
import { useClaimForReview } from "./use-reviewer-queue";

const Row = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: 12,
  borderRadius: 12,
  border: `1px solid ${EPOCUS_BORDER}`,
  backgroundColor: EPOCUS_WHITE,
  cursor: "pointer",
  "&:hover": { backgroundColor: EPOCUS_SELECTED_BG },
});

const Thumb = styled(Box)({
  width: 44,
  height: 44,
  borderRadius: 8,
  backgroundColor: EPOCUS_BG,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

const Info = styled(Box)({ flex: 1, minWidth: 0 });

const PatientName = styled(Typography)({
  fontSize: rem(14),
  fontWeight: 600,
  color: EPOCUS_TEXT_PRIMARY,
});

const Meta = styled(Typography)({
  fontSize: rem(12),
  color: EPOCUS_TEXT_SECONDARY,
});

const ExamChip = styled(Box)({
  padding: "3px 8px",
  borderRadius: 20,
  fontSize: rem(11),
  fontWeight: 600,
  backgroundColor: EPOCUS_BG,
  color: EPOCUS_TEXT_SECONDARY,
  whiteSpace: "nowrap",
});

const DraftTag = styled(Box)({
  padding: "3px 8px",
  borderRadius: 20,
  fontSize: rem(11),
  fontWeight: 700,
  backgroundColor: EPOCUS_STATUS_DRAFT_BG,
  color: EPOCUS_STATUS_DRAFT_TEXT,
  whiteSpace: "nowrap",
});

const PoolTag = styled(Box)({
  padding: "2px 6px",
  borderRadius: 4,
  fontSize: rem(10),
  fontWeight: 700,
  letterSpacing: 0.4,
  backgroundColor: EPOCUS_SELECTED_BG,
  color: EPOCUS_ICON_ACTIVE,
  border: `1px solid ${EPOCUS_BORDER}`,
});

const relativeTime = (date: string): string => {
  const time = new Date(date).getTime();
  if (!Number.isFinite(time)) return "";
  return `${formatDistanceToNow(new Date(time))} ago`;
};

export const ReviewerQueueRow: FC<{ entry: ReviewerQueueEntry }> = ({
  entry,
}) => {
  const { encounter, exams, fromPool, hasMyDraft } = entry;
  const navigate = useNavigate();
  const claim = useClaimForReview();

  const patientName =
    formatDicomPatientName(encounter.patientName) || "Unknown patient";
  const firstExam = exams[0];
  const ExamIcon = firstExam
    ? (EXAM_ICONS[firstExam.examType] ?? UltrasoundScanner)
    : UltrasoundScanner;

  /*
   * A claim that fails leaves the encounter in the pool for someone else, which
   * is recoverable; refusing to open it is not. Server time is unknown until
   * the first Apollo response, and `getCurrentTime` throws rather than guessing.
   */
  const open = async () => {
    if (fromPool) {
      try {
        await claim(encounter.id);
      } catch {}
    }
    navigate(linkToReviewQueueEntry(encounter.id, exams));
  };

  return (
    <Row
      role="button"
      tabIndex={0}
      aria-label={`Review ${patientName}`}
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          void open();
        }
      }}
    >
      <Thumb>
        <ExamIcon
          width={22}
          height={22}
          fill={EPOCUS_ICON_ACTIVE}
          aria-hidden="true"
        />
      </Thumb>
      <Info>
        <Box display="flex" alignItems="center" style={{ gap: 6 }}>
          <PatientName noWrap>{patientName}</PatientName>
          {fromPool && <PoolTag>Pool</PoolTag>}
        </Box>
        <Meta noWrap>
          {[encounter.operatorName, relativeTime(encounter.date)]
            .filter(Boolean)
            .join(" · ")}
        </Meta>
      </Info>
      {firstExam && (
        <ExamChip>
          {EXAM_LABELS[firstExam.examType] ?? firstExam.examType}
        </ExamChip>
      )}
      {hasMyDraft && <DraftTag>Draft</DraftTag>}
      <ChevronRightIcon
        style={{ color: EPOCUS_TEXT_SECONDARY, flexShrink: 0 }}
      />
    </Row>
  );
};
